export type FacetSelections = Record<string, string[]>;

export interface FiltersState {
    query: string;
    minPrice: string;
    maxPrice: string;
    subcategoryId: number | null;
    facets: FacetSelections;
}

export interface FacetValue {
    value: string;
    label: string;
    count: number;
    selected?: boolean;
    logo_url?: string;
}

export interface CatalogFacet {
    key: string;
    /** The merchant's title for this filter (Produkty → Filtry i sortowanie); absent = built-in wording. */
    label?: string;
    values: FacetValue[];
}

export interface FacetsResponse {
    total: number;
    price: { min: number | null; max: number | null };
    facets: CatalogFacet[];
}

export const EMPTY_FILTERS: FiltersState = {
    query: '',
    minPrice: '',
    maxPrice: '',
    subcategoryId: null,
    facets: {},
};

type SearchParamValue = string | string[] | undefined;

/** A price typed into the URL by hand or by a crawler ("10,50", "abc", "50zł"): Polish decimal
 *  comma becomes a dot, anything else that isn't a plain number is dropped — the API rejects it
 *  with 422 and the listing would silently come back empty. */
export function normalizePriceParam(value: string | undefined): string {
    const normalized = (value || '').trim().replace(/\s+/g, '').replace(',', '.');
    return /^\d+(\.\d+)?$/.test(normalized) ? normalized : '';
}

export function parseCatalogFilters(params: Record<string, SearchParamValue>): FiltersState {
    const first = (value: SearchParamValue) => Array.isArray(value) ? value[0] : value;
    const query = first(params.szukaj) || first(params.q) || first(params.search) || '';
    const minPrice = normalizePriceParam(first(params.min_price) || first(params.cena_od));
    const maxPrice = normalizePriceParam(first(params.max_price) || first(params.cena_do));
    const rawSubcategory = first(params.podkategoria) || first(params.subcategory_id);
    const parsedSubcategory = rawSubcategory ? Number.parseInt(rawSubcategory, 10) : NaN;
    const facets: FacetSelections = {};

    for (const [name, raw] of Object.entries(params)) {
        if (!name.startsWith('f.') || name.length <= 2) continue;
        const values = (Array.isArray(raw) ? raw : [raw])
            .flatMap((item) => String(item || '').split(','))
            .map((item) => item.trim())
            .filter(Boolean);
        if (values.length) facets[name.slice(2)] = Array.from(new Set(values));
    }

    return {
        query,
        minPrice,
        maxPrice,
        subcategoryId: Number.isFinite(parsedSubcategory) ? parsedSubcategory : null,
        facets,
    };
}

export function appendCatalogFilters(params: URLSearchParams, filters: FiltersState): URLSearchParams {
    for (const key of Array.from(params.keys())) {
        if (key.startsWith('f.')) params.delete(key);
    }
    ['szukaj', 'search', 'q', 'cena_od', 'min_price', 'cena_do', 'max_price', 'podkategoria', 'subcategory_id']
        .forEach((key) => params.delete(key));

    if (filters.query.trim()) params.set('q', filters.query.trim());
    if (filters.minPrice) params.set('min_price', filters.minPrice);
    if (filters.maxPrice) params.set('max_price', filters.maxPrice);
    if (filters.subcategoryId !== null) params.set('podkategoria', String(filters.subcategoryId));
    for (const [key, values] of Object.entries(filters.facets).sort(([a], [b]) => a.localeCompare(b))) {
        if (values.length) params.set(`f.${key}`, values.join(','));
    }
    return params;
}

export function appendFacetFilters(params: URLSearchParams, facets: FacetSelections): URLSearchParams {
    for (const [key, values] of Object.entries(facets)) {
        if (values.length) params.set(`f.${key}`, values.join(','));
    }
    return params;
}

export function filtersKey(filters: FiltersState): string {
    const facets = Object.entries(filters.facets)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, values]) => `${key}:${[...values].sort().join(',')}`)
        .join('|');
    return `${filters.query}|${filters.minPrice}|${filters.maxPrice}|${filters.subcategoryId ?? ''}|${facets}`;
}

export function activeFilterCount(filters: FiltersState): number {
    return Number(Boolean(filters.query.trim()))
        + Number(Boolean(filters.minPrice || filters.maxPrice))
        + Number(filters.subcategoryId !== null)
        + Object.values(filters.facets).reduce((sum, values) => sum + values.length, 0);
}

export function toggleFacetValue(facets: FacetSelections, key: string, value: string): FacetSelections {
    const selected = facets[key] || [];
    const nextValues = selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value];
    const next = { ...facets };
    if (nextValues.length) next[key] = nextValues;
    else delete next[key];
    return next;
}

export function humanizeFacetKey(key: string): string {
    return key.replace(/^@/, '').replace(/[-_]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}
